import timezones from './timezones';

export interface ResolvedTimeZone {
  name: string;
  fallback: boolean;
  configured: string;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(zone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(zone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: zone, hour12: false,
      year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric'
    });
    formatters.set(zone, formatter);
  }
  return formatter;
}

function isSupported(zone: string): boolean {
  try {
    formatterFor(zone);
    return true;
  } catch (error) {
    if (error instanceof RangeError) {
      return false;
    }
    throw error;
  }
}

// Strefy zmieniają przesunięcie na granicach 15-minutowych slotów UTC, więc w obrębie slotu jest ono stałe.
const OFFSET_SLOT_MS = 15 * 60 * 1000;
const offsets = new Map<string, number>();

function offsetMs(ms: number, zone: string): number {
  const slotStart = Math.floor(ms / OFFSET_SLOT_MS) * OFFSET_SLOT_MS;
  const key = zone + '|' + slotStart;
  let offset = offsets.get(key);
  if (offset === undefined) {
    const parts: { [type: string]: number } = {};
    formatterFor(zone).formatToParts(slotStart).forEach(part => parts[part.type] = Number(part.value));
    offset = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour % 24, parts.minute, parts.second) - slotStart;
    offsets.set(key, offset);
  }
  return offset;
}

export function resolveTimeZone(configured: string, fallbackZone: string): ResolvedTimeZone {
  const usable = !!configured && timezones.indexOf(configured) !== -1 && isSupported(configured);
  return { name: usable ? configured : fallbackZone, fallback: !usable, configured: configured || '' };
}

/** Date, którego lokalne gettery (getHours, getDate…) pokazują zegar strefy `zone` — to nie ta sama chwila co `ms`. */
export function zonedDate(ms: number, zone?: string): Date {
  const wall = new Date(ms + offsetMs(ms, zone));
  return new Date(wall.getUTCFullYear(), wall.getUTCMonth(), wall.getUTCDate(),
    wall.getUTCHours(), wall.getUTCMinutes(), wall.getUTCSeconds(), wall.getUTCMilliseconds());
}

export function zonedInstant(wallClock: Date, zone?: string): number {
  const asUtc = Date.UTC(wallClock.getFullYear(), wallClock.getMonth(), wallClock.getDate(),
    wallClock.getHours(), wallClock.getMinutes(), wallClock.getSeconds(), wallClock.getMilliseconds());
  return asUtc - offsetMs(asUtc - offsetMs(asUtc, zone), zone);
}

export function zoneOffset(ms: number, zone?: string): string {
  const minutes = Math.round(offsetMs(ms, zone) / 60000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (minutes < 0 ? '-' : '+') + pad(Math.floor(Math.abs(minutes) / 60)) + pad(Math.abs(minutes) % 60);
}
